/**
 * Central error tracking (Sentry) for the dashboard. Off unless this is a
 * production build with VITE_SENTRY_DSN set. The SDK loads lazily after first
 * paint, and every call is a no-op when disabled. Events carry the staff id and
 * the API's X-Request-ID (to find the matching server log line), never an
 * e-mail, token or request body.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN;
export const errorTrackingEnabled = !!dsn && import.meta.env.PROD;

type SentryModule = typeof import("@sentry/react");
let ready: Promise<SentryModule | null> | null = null;

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

function load(): Promise<SentryModule | null> {
  ready ??= (async () => {
    if (!errorTrackingEnabled) return null;
    try {
      const Sentry = await import("@sentry/react");
      Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        // SDK 11 replaced sendDefaultPii with per-category switches: collect none.
        dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false },
        // Errors only: no performance tracing or session replay.
        tracesSampleRate: 0,
        beforeSend(event) {
          // API error messages may quote an address (e.g. "user a@b.c already exists").
          for (const ex of event.exception?.values ?? []) if (ex.value) ex.value = ex.value.replace(EMAIL, "[email]");
          if (event.message) event.message = event.message.replace(EMAIL, "[email]");
          return event;
        },
      });
      return Sentry;
    } catch (err) {
      console.warn("error tracking unavailable", err);
      return null;
    }
  })();
  return ready;
}

/** Starts tracking once the browser is idle (call once at startup). */
export function initErrorTracking(): void {
  if (!errorTrackingEnabled) return;
  const start = () => void load();
  if ("requestIdleCallback" in window) window.requestIdleCallback(start, { timeout: 3000 });
  else setTimeout(start, 1500);
}

/** Ties events to the signed-in staff member by internal id only. */
export async function identifyForErrors(staffId: string | null): Promise<void> {
  const s = await load();
  s?.setUser(staffId ? { id: staffId } : null);
}

/** Reports an error; requestId (from an API problem) links it to the server logs. */
export async function captureError(error: unknown, context: { requestId?: string; source: string }): Promise<void> {
  const s = await load();
  if (!s) return;
  s.withScope((scope) => {
    scope.setTag("source", context.source);
    if (context.requestId) scope.setTag("request_id", context.requestId);
    s.captureException(error);
  });
}
